import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal, Output, EventEmitter, Input, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of, EMPTY } from 'rxjs';
import { catchError, expand, map, reduce, take } from 'rxjs/operators';

import { MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
// @ts-ignore
import reshaper from 'arabic-persian-reshaper';
// @ts-ignore
import * as bidi from 'bidi-js';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { CoreService } from '../../services/core.service';
import { ContentSiteI, SocialPlatformI } from '../../interfaces/core.interface';

type SortDir = 'asc' | 'desc';

type PostRow = {
  id: string;
  posting_queue_id: number;
  tag: string;
  social_platform_id: number;
  published: string;
  platform: string;
  format: string;
  title: string;
  link: string;
  reach: number;
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  badges: string[];
  er: number;
};
type ActiveChip =
  | { type: 'period'; label: string }
  | { type: 'text'; label: string }
  | { type: 'format'; label: string }
  | { type: 'metric'; key: MetricKey; label: string };

type ColumnDef = { key: keyof PostRow | 'badges'; label: string; tooltip?: string };
type MetricKey = 'reach' | 'impressions' | 'likes' | 'comments' | 'shares' | 'clicks' | 'er';

@Component({
  selector: 'app-query-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatSnackBarModule,
  ],
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.scss'],
})
export class QueryBuilderComponent implements OnInit {
  constructor(private coreService: CoreService, private router: Router, private snack: MatSnackBar) { }

  @Output() goMetrics = new EventEmitter<void>();
  @Input() viewMode!: Signal<'query' | 'metrics'>;
  @Input() setView!: (mode: 'query' | 'metrics') => void;
  // =========================
  // Dropdowns
  // =========================
  showDropdowns = signal(true);
  companyID = signal<number>(0);
  sites = signal<ContentSiteI[]>([]);
  platformsAll = signal<SocialPlatformI[]>([]);
  selectedSiteId = signal<number | null>(null);
  selectedPlatformId = signal<number | null>(null);
  selectedPeriod = signal<'days_28' | 'week' | 'custom'>('days_28');
  customFrom = signal('2024-04-01');
  customTo = signal('2024-04-30');

  // =========================
  // Static UX
  // =========================
  syncedAtUtc = signal<string>('');
  displayTz = signal<string>('Europe/Athens');

  textQuery = signal<string>('');
  selectedPostPerformance = signal<any | null>(null);
  loadingPostPerformance = signal<boolean>(false);
  selectedPost = signal<PostRow | null>(null);

  // format
  formatOptions = signal<string[]>([]);
  selectedFormat = signal<string | null>(null);

  //metric logic or and
  metricLogic = signal<'and' | 'or'>('and');
  setMetricLogic(v: 'and' | 'or') {
    if (this.metricLogic() === v) return;
    this.metricLogic.set(v);
    this.pageIndex.set(0);
    this.fetchPosts();
  }


  formatLabelMap: Record<string, string> = {
    fbfirstcomment: 'Facebook Photo Post (1st comment)',
    fbphotopost: 'Facebook Photo Post',
    fbreel: 'Facebook Reel Post',
    fbvideo: 'Facebook Video Post',
    photolink: 'Facebook Link Post',

    fbstoryphoto: 'Facebook Story Photo',
    fbstoryvideo: 'Facebook Story Video',
    fbstoryreel: 'Facebook Story Reel',

    igphotopost: 'Instagram Photo Post',
    igvideo: 'Instagram Video Post',
    igreel: 'Instagram Reel Post',
    igcarousel: 'Instagram Carousel Post',
    igstoryvideo: 'Instagram Story Video',
    igstoryphoto: 'Instagram Story Photo',
    igstoryreel: 'Instagram Story Reel',

    tiktokvideo: 'TikTok Video Post',
    tiktokreel: 'TikTok Reel Post',
    tiktokcarousel: 'TikTok Carousel Post',
    tiktokstoryphoto: 'TikTok Story Photo',
    tiktokstoryvideo: 'TikTok Story Video',
    tiktokstoryreel: 'TikTok Story Reel',
  };

  formatLabel(f: string): string {
    return this.formatLabelMap[f] ?? f;
  }

  // filtered platforms for the selected site
  filteredPlatforms = computed(() => {
    const siteId = this.selectedSiteId();
    const all = this.platformsAll();
    if (!siteId) return [];

    return all.filter((p) => {
      if (p.site_id !== siteId) return false;
      const name = (p.platform || '').toLowerCase().trim();
      return (
        name.includes('facebook') ||
        name.includes('instagram') ||
        name.includes('insta') ||
        name === 'ig'
      );
    });
  });

  ngOnInit(): void {
    this.companyID.set(this.coreService.user?.companyID || 0);
    this.bootstrapDropdowns();
  }

  private bootstrapDropdowns(): void {
    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites.set((sites as ContentSiteI[]) || []);
      this.platformsAll.set((platforms as SocialPlatformI[]) || []);
      const firstSite = this.sites().length ? this.sites()[0] : null;
      this.selectedSiteId.set(firstSite?.ID ?? null);
      this.setDefaultPlatformForSite();
      this.loadVisibleColumns();
      this.syncSelectedMetricWithVisibleColumns();
      this.loadFormats();
      this.pageIndex.set(0);
      this.fetchPosts();
    });
  }

  private setDefaultPlatformForSite(): void {
    const list = this.filteredPlatforms();
    if (!list.length) {
      this.selectedPlatformId.set(null);
      return;
    }
    const fb = list.find((p) => (p.platform || '').toLowerCase().includes('facebook'));
    this.selectedPlatformId.set((fb ?? list[0]).id);
  }

  onSiteChange(siteId: number): void {
    this.selectedSiteId.set(siteId);
    this.setDefaultPlatformForSite();
    this.loadFormats();
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  onPlatformChange(platformId: number): void {
    this.selectedPlatformId.set(platformId);
    this.loadFormats();
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  onPeriodChange(period: 'days_28' | 'week' | 'custom'): void {
    this.selectedPeriod.set(period);
    this.loadFormats();
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  // Columns
  columns = signal<ColumnDef[]>([
    { key: 'published', label: 'Published', tooltip: 'Post publish timestamp.' },
    { key: 'platform', label: 'Platform', tooltip: 'Social network (e.g., Facebook/Instagram).' },
    { key: 'format', label: 'Format', tooltip: 'Post format/tag (e.g., Reel/Story/Post).' },
    { key: 'title', label: 'Title', tooltip: 'Post title/description.' },
    { key: 'reach', label: 'Reach', tooltip: 'Unique accounts that saw the post.' },
    { key: 'impressions', label: 'Impressions', tooltip: 'Total times the post was shown.' },
    { key: 'likes', label: 'Likes', tooltip: 'Total likes/reactions.' },
    { key: 'comments', label: 'Comments', tooltip: 'Total comments.' },
    { key: 'shares', label: 'Shares', tooltip: 'Total shares.' },
    { key: 'clicks', label: 'Clicks', tooltip: 'Total clicks (link/CTA clicks depending on platform).' },
    { key: 'er', label: 'ER %', tooltip: 'ER = (likes+comments+shares+clicks) / reach * 100' },
    { key: 'badges', label: 'Badges', tooltip: 'Badges rules:\nER = (likes+comments+shares+clicks)/reach*100.\nLow ER ≤ 0.5%, Good ER ≥ 2%.\nShared: shares ≥ 3, Clicked: clicks ≥ 3.' },
  ]);

  visibleColumns = signal(
    new Set<keyof PostRow | 'badges'>([
      'published',
      'platform',
      'format',
      'title',
      'reach',
      'impressions',
      'likes',
      'comments',
      'shares',
      'clicks',
      'er',
      'badges',
    ])
  );

  // Keeping sort UI
  sort = signal<Array<{ id: string; field: keyof PostRow; dir: SortDir }>>([
    { id: 's1', field: 'published', dir: 'desc' },
  ]);

  private readonly sortableKeys = new Set<keyof PostRow>([
    'published',
    'reach',
    'impressions',
    'likes',
    'comments',
    'shares',
    'clicks',
    'er',
  ]);
  isSortableColumn(key: any): key is keyof PostRow {
    return this.sortableKeys.has(key);
  }

  activeSort = computed(() => this.sort()?.[0] ?? null);

  sortArrow = computed(() => {
    const s = this.activeSort();
    return s?.dir === 'asc' ? '▲' : '▼';
  });

  // Selection stored across pages (do NOT reset on fetch)
  selectedIds = signal(new Set<string>());

  // Data
  rows = signal<PostRow[]>([]);
  sortedRows = computed(() => {
    const rows = [...(this.rows() || [])];
    const s = this.activeSort();
    if (!s) return rows;

    const { field, dir } = s;

    const numericKeys = new Set([
      'reach', 'impressions', 'likes', 'comments', 'shares', 'clicks', 'er'
    ]);

    const toNum = (v: any) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : NaN;
    };

    const toDateMs = (v: any) => {
      const ms = new Date(v).getTime();
      return Number.isFinite(ms) ? ms : NaN;
    };

    const cmp = (a: any, b: any) => {
      const av = a?.[field];
      const bv = b?.[field];

      if (field === 'published') {
        const ad = toDateMs(av);
        const bd = toDateMs(bv);
        if (Number.isNaN(ad) && Number.isNaN(bd)) return 0;
        if (Number.isNaN(ad)) return 1;
        if (Number.isNaN(bd)) return -1;
        return ad - bd;
      }

      if (numericKeys.has(field as any)) {
        const an = toNum(av);
        const bn = toNum(bv);
        if (Number.isNaN(an) && Number.isNaN(bn)) return 0;
        if (Number.isNaN(an)) return 1;
        if (Number.isNaN(bn)) return -1;
        return an - bn;
      }

      const as = (av ?? '').toString().toLowerCase();
      const bs = (bv ?? '').toString().toLowerCase();
      return as.localeCompare(bs);
    };

    rows.sort((a, b) => (dir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return rows;
  });


  // base list (static)
  metricOptionsBase = [
    { key: 'likes' as const, label: 'Likes' },
    { key: 'comments' as const, label: 'Comments' },
    { key: 'shares' as const, label: 'Shares' },
    { key: 'clicks' as const, label: 'Clicks' },
    { key: 'reach' as const, label: 'Reach' },
    { key: 'impressions' as const, label: 'Impressions' },
    { key: 'er' as const, label: 'ER %' },
  ];

  metricOptions = computed(() =>
    this.metricOptionsBase.filter(m => this.visibleColumns().has(m.key))
  );


  selectedMetricKey = signal<MetricKey | null>('likes');
  metricMin = signal<number | null>(null);
  metricMax = signal<number | null>(null);

  // applied metric filters sent to backend
  metricFilters = signal<Partial<Record<MetricKey, { min?: number; max?: number }>>>({});

  hasMetricFilter = computed(() => Object.keys(this.metricFilters() || {}).length > 0);

  activeChips = computed<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];

    // period
    const p = this.selectedPeriod();
    if (p === 'custom') {
      chips.push({ type: 'period', label: `Date: ${this.customFrom()} → ${this.customTo()}` });
    } else if (p === 'days_28') {
      chips.push({ type: 'period', label: 'Date: Last 30d' });
    } else {
      chips.push({ type: 'period', label: 'Date: Last 7d' });
    }


    // text
    const q = (this.textQuery() || '').trim();
    if (q) chips.push({ type: 'text', label: `Text: "${q}"` });

    // format
    const fmt = this.selectedFormat();
    if (fmt) chips.push({ type: 'format', label: `Format: ${this.formatLabel(fmt)}` });

    // metric filters
    const mf = this.metricFilters() || {};
    for (const k of Object.keys(mf) as MetricKey[]) {
      const v = mf[k];
      const labelBase = this.metricOptions().find(x => x.key === k)?.label ?? k;

      const parts: string[] = [];
      if (v?.min != null) parts.push(`≥ ${v.min}`);
      if (v?.max != null) parts.push(`≤ ${v.max}`);

      if (parts.length) {
        chips.push({
          type: 'metric',
          key: k,
          label: `${labelBase}: ${parts.join(' & ')}`,
        });
      }
    }

    return chips;
  });


  // Selection helpers
  get allSelected(): boolean {
    const pageRows = this.sortedRows();
    const sel = this.selectedIds();
    return pageRows.length > 0 && pageRows.every((r) => sel.has(r.id));
  }

  toggleAll() {
    const pageRows = this.sortedRows();
    if (!pageRows.length) return;

    const next = new Set(this.selectedIds());
    const allOnPageSelected = pageRows.every((r) => next.has(r.id));

    if (allOnPageSelected) pageRows.forEach((r) => next.delete(r.id));
    else pageRows.forEach((r) => next.add(r.id));

    this.selectedIds.set(next);
  }

  toggleSelectRow(row: PostRow) {
    const next = new Set(this.selectedIds());
    if (next.has(row.id)) next.delete(row.id);
    else next.add(row.id);
    this.selectedIds.set(next);
  }

  cellValue(row: PostRow, key: keyof PostRow) {
    return row[key];
  }

  toggleColumn(key: keyof PostRow | 'badges') {
    const next = new Set(this.visibleColumns());
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.visibleColumns.set(next);
    this.saveVisibleColumns();

    const selected = this.selectedMetricKey();
    const selectedStillVisible = selected ? this.visibleColumns().has(selected) : false;

    if (!selectedStillVisible) {
      const first = this.metricOptions()[0]?.key ?? null;
      this.selectedMetricKey.set(first);
      this.metricMin.set(null);
      this.metricMax.set(null);

      const mf = { ...(this.metricFilters() || {}) } as any;
      if (selected) delete mf[selected];
      this.metricFilters.set(mf);

      this.pageIndex.set(0);
      this.fetchPosts();
    }
  }


  // Post performance (kept same)
  toggleRow(row: PostRow) {
    const siteId = this.selectedSiteId();
    const platformId = row.social_platform_id ?? this.selectedPlatformId();
    if (!platformId || !row.posting_queue_id || !row.tag || !this.companyID()) {
      console.log('Missing payload fields:', {
        company_id: this.companyID(),
        contentSiteId: siteId,
        social_platform_id: platformId,
        posting_queue_id: row.posting_queue_id,
        tag: row.tag,
        row,
      });
      return;
    }

    const payload = {
      company_id: this.companyID(),
      contentSiteId: siteId,
      social_platform_id: platformId,
      posting_queue_id: row.posting_queue_id,
      tag: row.tag,
    };

    this.coreService
      .postData('feedsync/preview-post-performance', payload)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.selectedPostPerformance.set(res);
      });
  }

  toggleSort(field: any) {
    if (!this.isSortableColumn(field)) return;

    const f = field as keyof PostRow;
    const current = this.activeSort();

    let nextDir: SortDir;

    // switching column -> "clean restart"
    if (!current || current.field !== f) {
      nextDir = (f === 'published') ? 'desc' : 'desc'; // choose your defaults
    } else {
      // same column -> flip
      nextDir = current.dir === 'asc' ? 'desc' : 'asc';
    }

    // always keep ONLY one active sort
    this.sort.set([{ id: 's1', field: f, dir: nextDir }]);

    // reset to first page whenever sorting changes
    this.pageIndex.set(0);

    queueMicrotask(() => this.fetchPosts());
  }

  fmtNumber(n: number) {
    return new Intl.NumberFormat('en-US').format(n);
  }

  // API loaders
  private loadSites() {
    const cid = this.companyID();
    return this.coreService.getData(`/querybuilder/websites-feeds-dropdown/${cid}`).pipe(
      take(1),
      map((res: any) => (res.data || []).filter((s: any) => s.hasFeed)),
      catchError(() => of([]))
    );
  }

  private loadPlatforms() {
    return this.coreService.getData('/querybuilder/platform-dropdown').pipe(
      take(1),
      map((res: any) =>
        (res.data as SocialPlatformI[]).filter(
          (p) => p.isApproved === 'Approved' && !!p.access_token
        )
      ),
      catchError(() => of([]))
    );
  }

  // Server pagination
  pageSize = signal<number>(12);
  pageIndex = signal<number>(0);
  totalItems = signal<number>(0);

  // table rows already paged by backend
  pagedRows = computed(() => this.sortedRows());
  onPageChange(event: any) {
    this.pageIndex.set(event.pageIndex ?? 0);
    if (event.pageSize) this.pageSize.set(event.pageSize);
    this.fetchPosts();
  }

  // Fetch posts (backend) — includes metric_filters in payload
  private fetchPosts(): void {
    const payload: any = {
      company_id: this.companyID(),
      site_id: this.selectedSiteId(),
      platform_id: this.selectedPlatformId(),
      period: this.selectedPeriod(),
      text_query: (this.textQuery() || '').trim() || null,
      format: this.selectedFormat(),
      metric_filters: this.metricFilters(),
      metric_logic: this.metricLogic(),
      page: this.pageIndex() + 1,
      per_page: this.pageSize(),

      sort_field: this.sortFieldForApi(),
      sort_dir: this.sortDirForApi(),
    };

    if (payload.period === 'custom') {
      payload.from_date = this.customFrom();
      payload.to_date = this.customTo();
    }

    this.coreService
      .postData('querybuilder/report-results', payload)
      .pipe(take(1), catchError(() => of({ status: 'error' })))
      .subscribe((res: any) => {
        if (res?.status !== 'success') {
          this.rows.set([]);
          this.totalItems.set(0);
          return;
        }

        this.syncedAtUtc.set(res?.data?.latest_update ?? '');

        const posts = (res?.data?.posts ?? []).map((p: any) => ({
          ...p,
          badges: Array.isArray(p.badges) ? p.badges : [],
          id: (p.id ?? p.posting_queue_id)?.toString(),
        }));

        this.rows.set(posts);

        this.totalItems.set(res?.data?.pagination?.total ?? 0);
      });
  }

  // click title -> redirect preview
  goToPreview(r: PostRow) {
    const id = r.posting_queue_id ?? r.id;
    this.router.navigate(['/preview-post', r.tag, id]);
  }

  // limit title words
  title3Words(title: string): string {
    if (!title) return '';
    const words = title.trim().split(/\s+/);
    return words.slice(0, 4).join(' ') + (words.length > 3 ? '…' : '');
  }

  // search
  applySearch() {
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  clearSearch() {
    this.textQuery.set('');
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  // Metric filter helpers for UI
  selectMetric(key: MetricKey | null) {
    this.selectedMetricKey.set(key);
    const existing = (this.metricFilters() || {})[key as MetricKey];
    this.metricMin.set(existing?.min ?? null);
    this.metricMax.set(existing?.max ?? null);
  }

  applyMetricFilter() {
    const key = this.selectedMetricKey();
    if (!key) return;

    const min = this.metricMin();
    const max = this.metricMax();

    const current = { ...(this.metricFilters() || {}) } as Partial<
      Record<MetricKey, { min?: number; max?: number }>
    >;

    if (min == null && max == null) {
      delete (current as any)[key];
    } else {
      (current as any)[key] = {};
      if (min != null) (current as any)[key].min = min;
      if (max != null) (current as any)[key].max = max;
    }

    this.metricFilters.set(current);
    this.metricMin.set(null);
    this.metricMax.set(null);
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  clearMetricFilter() {
    this.metricFilters.set({});
    this.metricMin.set(null);
    this.metricMax.set(null);
    this.pageIndex.set(0);
    this.fetchPosts();
  }

  private loadFormats(): void {
    const siteId = this.selectedSiteId();
    if (!siteId) {
      this.formatOptions.set([]);
      this.selectedFormat.set(null);
      return;
    }

    const period = this.selectedPeriod();

    let url =
      `/querybuilder/formats?company_id=${this.companyID()}` +
      `&site_id=${siteId}` +
      `&platform_id=${this.selectedPlatformId() ?? ''}` +
      `&period=${period}`;

    if (period === 'custom') {
      url += `&from_date=${this.customFrom()}&to_date=${this.customTo()}`;
    }

    this.coreService
      .getData(url)
      .pipe(take(1), catchError(() => of({ status: 'error', data: [] })))
      .subscribe((res: any) => {
        const list: string[] = res?.status === 'success' ? (res.data ?? []) : [];
        this.formatOptions.set(list);
        const current = this.selectedFormat();
        if (current && !list.includes(current)) {
          this.selectedFormat.set(null);
        }
      });
  }

  // custom dates
  onCustomFromChange(v: string) {
    this.customFrom.set(v);
    this.pageIndex.set(0);
    this.loadFormats();
    this.fetchPosts();
  }

  onCustomToChange(v: string) {
    this.customTo.set(v);
    this.pageIndex.set(0);
    this.loadFormats();
    this.fetchPosts();
  }

  // EXPORT HELPERS
  private buildPostsPayload(page: number, perPage: number) {
    const payload: any = {
      company_id: this.companyID(),
      site_id: this.selectedSiteId(),
      platform_id: this.selectedPlatformId(),
      period: this.selectedPeriod(),
      text_query: (this.textQuery() || '').trim() || null,
      format: this.selectedFormat(),
      metric_filters: this.metricFilters(),
      metric_logic: this.metricLogic(),
      page,
      per_page: perPage,

    };

    if (payload.period === 'custom') {
      payload.from_date = this.customFrom();
      payload.to_date = this.customTo();
      payload.sort_field = this.sortFieldForApi();
      payload.sort_dir = this.sortDirForApi();

    }
    payload.sort_field = this.activeSort()?.field ?? 'published';
    payload.sort_dir = this.activeSort()?.dir ?? 'desc';

    return payload;
  }

  private fetchAllPostsForExport(perPage = 200) {
    const firstPayload = this.buildPostsPayload(1, perPage);

    return this.coreService.postData('querybuilder/report-results', firstPayload).pipe(
      take(1),
      map((res: any) => {
        const posts = res?.status === 'success' ? (res.data?.posts ?? []) : [];
        const total = res?.data?.pagination?.total ?? posts.length;
        return { posts, total, page: 1, perPage };
      }),
      expand(({ posts, total, page, perPage }) => {
        const loaded = page * perPage;
        if (!posts.length || loaded >= total) return EMPTY;

        const nextPayload = this.buildPostsPayload(page + 1, perPage);
        return this.coreService.postData('querybuilder/report-results', nextPayload).pipe(
          take(1),
          map((res: any) => {
            const nextPosts = res?.status === 'success' ? (res.data?.posts ?? []) : [];
            const nextTotal = res?.data?.pagination?.total ?? total;
            return { posts: nextPosts, total: nextTotal, page: page + 1, perPage };
          })
        );
      }),
      map((x) => x.posts),
      reduce((acc, posts) => acc.concat(posts), [] as any[])
    );
  }

  private async getRowsForExport(): Promise<any[]> {
    const allRows: any[] = await new Promise((resolve) => {
      this.fetchAllPostsForExport(200).subscribe({
        next: (rows) => resolve(rows || []),
        error: () => resolve([]),
      });
    });

    const selected = this.selectedIds();
    return selected.size > 0 ? allRows.filter((r) => selected.has(r.id)) : allRows;
  }

 // PDF export (ALL pages)
  async exportPdf() {
    try {
      const rowsToExport = await this.getRowsForExport();
      if (!rowsToExport.length) {
        alert('No rows to export.');
        return;
      }

      const cols = this.columns().filter((c) => this.visibleColumns().has(c.key));
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

      await this.ensureGreekFont(doc);
      await this.ensureArabicFonts(doc);

      // Shared layout constants (align footer with logo right edge)
      const margin = 40;
      const pageNumFont = 'NotoSans';
      const pageNumSize = 9;
      const pageNumMarginBottom = 16;

      // ===== Title (left) =====
      doc.setFont('NotoSans', 'bold');
      doc.setFontSize(14);
      doc.text('Report', 40, 34);

      // ===== Logo (top-right) =====
      try {
        const logoDataUrl = await this.loadImageAsDataUrl('assets/images/publio_logo_pdf.png');
        const pageW = doc.internal.pageSize.getWidth();

        const logoSize = 80;
        const x = pageW - margin - logoSize;
        const yLogo = 16;

        doc.addImage(logoDataUrl, 'PNG', x, yLogo, logoSize, logoSize);
      } catch (e) {
        console.warn('Logo not added:', e);
      }

      doc.setFont('NotoSans', 'normal');
      doc.setFontSize(9);

      const leftX = 40;
      const labelW = 68;
      const valueX = leftX + labelW;

      const meta: Array<[string, string]> = [
        ['Website:', this.siteLabel()],
        ['Date range:', this.dateRangeLabel()],
        ['Last update:', this.syncedAtUtc() || '—'],
        ['Timezone:', this.displayTz()],
      ];

      if (this.hasMetricFilter()) {
        meta.push(['Filters:', this.performanceFiltersLabel()]);
      }

      let y = 50;
      const lineH = 12;
      for (const [label, value] of meta) {
        doc.text(label, leftX, y);
        const out = this.isArabic(value) ? this.fixArabicForPdf(value) : value;
        doc.text(out, valueX, y);
        y += lineH;
      }

      const tableStartY = y + 8;
      // ===== Table data =====
      const head = [
        cols.map((c) => (this.isArabic(c.label) ? this.fixArabicForPdf(c.label) : c.label)),
      ];

      const body = rowsToExport.map((r) =>
        cols.map((c) => {
          const v: any = r?.[c.key];
          const s = typeof v === 'number' ? this.fmtNumber(v) : (v ?? '').toString();
          return this.isArabic(s) ? this.fixArabicForPdf(s) : s;
        })
      );

      // ===== Table render =====
      autoTable(doc, {
        head,
        body,
        startY: tableStartY,

        //space for footer page number
        margin: { bottom: 28 },

        styles: { font: 'NotoSans', fontStyle: 'normal', fontSize: 8, cellPadding: 4 },
        headStyles: { font: 'NotoSans', fontStyle: 'normal' },

        didParseCell: (data) => {
          data.cell.styles.fontStyle = 'normal';

          const txt = Array.isArray(data.cell.text)
            ? data.cell.text.join(' ')
            : (data.cell.text ?? '');

          if (this.isArabic(txt)) {
            data.cell.styles.halign = 'right';
            data.cell.styles.font = 'NotoArabic';
          } else {
            data.cell.styles.font = 'NotoSans';
          }
        },

        willDrawCell: (data) => {
          const font = (data.cell.styles.font as string) || 'NotoSans';
          doc.setFont(font, 'normal');
        },

        didDrawCell: () => {
          doc.setFont('NotoSans', 'normal');
        },
      });

      //Add page numbers
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        doc.setFont(pageNumFont, 'normal');
        doc.setFontSize(pageNumSize);

        doc.text(
          `Page ${i} of ${pageCount}`,
          pageW - margin,
          pageH - pageNumMarginBottom,
          { align: 'right' }
        );
      }

      doc.save(this.reportFilename('pdf'));
    } catch (e) {
      console.error('PDF export failed:', e);
      alert('PDF export failed. Check console.');
    }
  }



  // Excel export (ALL pages)
  async exportExcel() {
    const cols = this.columns().filter((c) => this.visibleColumns().has(c.key));
    const rowsToExport = await this.getRowsForExport();

    if (!rowsToExport.length) {
      alert('No rows to export.');
      return;
    }

    const data = rowsToExport.map((r) => {
      const obj: Record<string, any> = {};
      for (const c of cols) obj[c.label] = r?.[c.key] ?? '';
      return obj;
    });

    const headerRows = [
      ['Social Posts Report'],
      [`Website: ${this.siteLabel()}`],
      [`Date range: ${this.dateRangeLabel()}`],
      [`Last update: ${this.syncedAtUtc() || '—'}`],
      [`Timezone: ${this.displayTz()}`],
      [`Performance filters: ${this.performanceFiltersLabel()}`],
      [],
    ];

    const ws = XLSX.utils.aoa_to_sheet(headerRows);
    XLSX.utils.sheet_add_json(ws, data, { origin: 'A7' });
    ws['!cols'] = cols.map((c) => ({ wch: Math.max(10, c.label.length + 2) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Results');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      this.reportFilename('xlsx')
    );
  }

  bulkExportPdf() {
    this.exportPdf();
  }

  bulkExportExcel() {
    this.exportExcel();
  }

  openSavedView() {
    this.saveVisibleColumns();
    this.snack.open('Saved View Stored!', 'OK', {
      duration: 2500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['qb-snack-success'],
    });
  }

  // Saved View
  private savedViewKey(): string {
    const cid = this.companyID() || 0;
    return `qb_visible_columns:${cid}`;
  }

  private saveVisibleColumns(): void {
    const cols = Array.from(this.visibleColumns());
    localStorage.setItem(this.savedViewKey(), JSON.stringify(cols));
  }

  private loadVisibleColumns(): void {
    const raw = localStorage.getItem(this.savedViewKey());
    if (!raw) return;

    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) this.visibleColumns.set(new Set(arr));
    } catch {
    }
  }

  private siteLabel(): string {
    const selectedId = this.selectedSiteId();
    if (selectedId == null) return '—';

    const site = (this.sites() as any[]).find(
      (s) => Number(s?.ID) === Number(selectedId)
    );
    return site?.Domain || `Site #${selectedId}`;
  }

  private dateRangeLabel(): string {
    const p = this.selectedPeriod();
    if (p === 'week') return 'Last 7 days';
    if (p === 'days_28') return 'Last 30 days';
    return `${this.customFrom()} → ${this.customTo()}`;
  }
  private performanceFiltersLabel(): string {
    const mf = this.metricFilters() || {};
    const keys = Object.keys(mf) as MetricKey[];
    if (!keys.length) return '—';

    const parts = keys.map((k) => {
      const v = mf[k];
      const label = this.metricOptionsBase.find(x => x.key === k)?.label ?? k;

      const p: string[] = [];
      if (v?.min != null) p.push(`≥ ${v.min}`);
      if (v?.max != null) p.push(`≤ ${v.max}`);

      return `${label} ${p.join(' & ')}`.trim();
    });

    const logic = keys.length > 1 ? ` (${this.metricLogic().toUpperCase()})` : '';
    return parts.join(' | ') + logic;
  }


  // Fonts / Arabic helpers
  private async loadFontAsBase64(path: string): Promise<string> {
    const res = await fetch(path);
    if (!res.ok) throw new Error(`Font fetch failed: ${path} (${res.status})`);

    const buf = await res.arrayBuffer();

    const b = new Uint8Array(buf.slice(0, 4));
    const sig = String.fromCharCode(...b);

    const isOTF = sig === 'OTTO' || sig === 'ttcf';
    const isTTF = b[0] === 0x00 && b[1] === 0x01 && b[2] === 0x00 && b[3] === 0x00;

    if (!isOTF && !isTTF) {
      throw new Error(`Not a font file: ${path} (signature="${sig}")`);
    }

    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;

    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }

    return btoa(binary);
  }

  private async ensureGreekFont(doc: jsPDF) {
    const reg = await this.loadFontAsBase64('assets/fonts/greek/NotoSans-Regular.ttf');
    doc.addFileToVFS('NotoSans-Regular.ttf', reg);
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');

    const bold = await this.loadFontAsBase64('assets/fonts/greek/NotoSans-Bold.ttf');
    doc.addFileToVFS('NotoSans-Bold.ttf', bold);
    doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');

    doc.setFont('NotoSans', 'normal');
  }

  private async ensureArabicFonts(doc: jsPDF) {
    const reg = await this.loadFontAsBase64('assets/fonts/arabic/NotoNaskhArabic-Regular.ttf');
    doc.addFileToVFS('NotoNaskhArabic-Regular.ttf', reg);
    doc.addFont('NotoNaskhArabic-Regular.ttf', 'NotoArabic', 'normal');

    const bold = await this.loadFontAsBase64('assets/fonts/arabic/NotoNaskhArabic-Bold.ttf');
    doc.addFileToVFS('NotoNaskhArabic-Bold.ttf', bold);
    doc.addFont('NotoNaskhArabic-Bold.ttf', 'NotoArabic', 'bold');

    doc.setFont('NotoSans', 'normal');
  }

  private isArabic(text: string): boolean {
    return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text || '');
  }

  private fixArabicForPdf(text: string): string {
    if (!text) return '';

    const anyReshaper: any = reshaper as any;
    const reshapeFn =
      (typeof anyReshaper === 'function' ? anyReshaper : null) ||
      (typeof anyReshaper?.reshape === 'function' ? anyReshaper.reshape : null) ||
      (typeof anyReshaper?.default === 'function' ? anyReshaper.default : null) ||
      (typeof anyReshaper?.default?.reshape === 'function'
        ? anyReshaper.default.reshape
        : null);

    const shaped = reshapeFn ? reshapeFn(text) : text;

    const anyBidi: any = bidi as any;
    const visualFn =
      (typeof anyBidi?.getVisualString === 'function' ? anyBidi.getVisualString : null) ||
      (typeof anyBidi?.default?.getVisualString === 'function'
        ? anyBidi.default.getVisualString
        : null);

    return visualFn ? visualFn(shaped, { baseDir: 'R' }) : shaped;
  }

  private reportFilename(ext: 'pdf' | 'xlsx' = 'pdf'): string {
    const tz = this.displayTz() || 'Europe/Athens';
    const d = new Date();

    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);

    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';

    const dd = get('day');
    const mm = get('month');
    const yyyy = get('year');
    const HH = get('hour');
    const min = get('minute');

    return `Report ${dd}.${mm}.${yyyy} ${HH}:${min}.${ext}`;
  }

  removeChip(chip: ActiveChip) {
    switch (chip.type) {
      case 'period':
        if (this.selectedPeriod() === 'custom') {
          this.selectedPeriod.set('days_28'); // back to default
          this.loadFormats();
        }
        break;

      case 'text':
        this.textQuery.set('');
        break;

      case 'format':
        this.selectedFormat.set(null);
        this.loadFormats();
        break;

      case 'metric': {
        const current = { ...(this.metricFilters() || {}) } as Partial<
          Record<MetricKey, { min?: number; max?: number }>
        >;
        delete (current as any)[chip.key];
        this.metricFilters.set(current);

        if (this.selectedMetricKey() === chip.key) {
          this.metricMin.set(null);
          this.metricMax.set(null);
        }
        break;
      }
    }

    this.pageIndex.set(0);
    this.fetchPosts();
  }

  canRemoveChip(chip: ActiveChip): boolean {
    if (chip.type === 'period') return this.selectedPeriod() === 'custom';
    return true;
  }
  private syncSelectedMetricWithVisibleColumns() {
    const vis = this.visibleColumns();
    const selected = this.selectedMetricKey();

    if (selected && vis.has(selected)) return;

    const first = this.metricOptions()[0]?.key ?? null;
    this.selectedMetricKey.set(first);
    this.metricMin.set(null);
    this.metricMax.set(null);

    const mf = { ...(this.metricFilters() || {}) } as any;
    for (const k of Object.keys(mf)) {
      if (!vis.has(k as any)) delete mf[k];
    }
    this.metricFilters.set(mf);
  }

  metricPlaceholderMin(): string {
    const k = this.selectedMetricKey();
    if (!k) return 'Enter min';

    switch (k) {
      case 'er': return 'Min ER (e.g. 2.00%)';
      case 'reach': return 'Min reach (e.g. 1,000)';
      case 'impressions': return 'Min impressions (e.g. 5,000)';
      case 'likes': return 'Min likes (e.g. 50)';
      case 'comments': return 'Min comments (e.g. 10)';
      case 'shares': return 'Min shares (e.g. 5)';
      case 'clicks': return 'Min clicks (e.g. 20)';
      default: return 'Enter min';
    }
  }

  metricPlaceholderMax(): string {
    const k = this.selectedMetricKey();
    if (!k) return 'Enter max';

    switch (k) {
      case 'er': return 'Max ER (optional)';
      case 'reach': return 'Max reach (optional)';
      case 'impressions': return 'Max impressions (optional)';
      case 'likes': return 'Max likes (optional)';
      case 'comments': return 'Max comments (optional)';
      case 'shares': return 'Max shares (optional)';
      case 'clicks': return 'Max clicks (optional)';
      default: return 'Enter max';
    }
  }
  fmtPct(n: any, decimals = 2): string {
    const num = Number(n);
    if (n == null || Number.isNaN(num)) return '—';
    return `${num.toFixed(decimals)}%`;
  }

  private sortFieldForApi(): string | null {
    const s = this.activeSort();
    if (!s) return null;
    return s.field;
  }

  private sortDirForApi(): SortDir {
    const s = this.activeSort();
    return (s?.dir ?? 'desc');
  }

  private async loadImageAsDataUrl(path: string): Promise<string> {
    const res = await fetch(path);
    if (!res.ok) throw new Error(`Image fetch failed: ${path} (${res.status})`);
    const blob = await res.blob();

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  }


}
